import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { usePageView } from "src/hooks/use-page-view";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import {
  approvalEditSlice,
  approvalEditThunks,
} from "src/features/approval-edit/approvalEditSlice";
import ApprovalItemsTable from "src/sections/approval-edit/approval-edit";
import { PageContainerSide } from "src/components/container/page-container-side";
import { ApprovalStageStepper } from "src/sections/approval-edit/approval-stage-stepper";
import { Stack, Typography, Skeleton, Divider } from "@mui/material";
import { ApprovalLog } from "src/components/approval/approval-log";
import { TutorialVideoButton } from "src/components/tutorial/tutorial-video-button";
import { tutorialVideoById } from "src/constants/tutorials";

export const Route = createFileRoute(
  "/_authenticated/workspaces/$wrk_id/aprovacao/$apv_id/detalhes/",
)({
  component: () => <Page />,
});

const Page = () => {
  usePageView();
  const dispatch = useAppDispatch();
  const approval = useAppSelector(approvalEditSlice.selectors.approval);
  const status = useAppSelector(approvalEditSlice.selectors.status);
  const isLoading = status === "loading";
  const { apv_id, wrk_id } = Route.useParams();

  useEffect(() => {
    dispatch(approvalEditThunks.fetchApproval({ apv_id }));
  }, [apv_id, dispatch]);

  return (
    <PageContainerSide
      title={!isLoading ? approval?.apv_name : "Carregando..."}
      subtitle={
        <Stack>
          {isLoading ? (
            <Skeleton width={120} height={24} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
              {approval?.apv_status.sts_name}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton width={180} height={18} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {approval?.apv_status.statusType.stp_description}
            </Typography>
          )}
        </Stack>
      }
      breadcrumbs={[
        {
          href: `/workspaces/${wrk_id}`,
          label: "Workspaces",
        },
        {
          href: `/workspaces/${wrk_id}/aprovacao/${apv_id}`,
          label: "Aprovação",
        },
        {
          href: `/workspaces/${wrk_id}/aprovacao/${apv_id}/detalhes`,
          label: "Detalhes",
        },
      ]}
      leftNode={
        <Stack direction="column" spacing={3} flex={1} flexGrow={1}>
          <ApprovalStageStepper />
          {approval && <ApprovalLog approval={approval} />}
          <Stack direction="column" spacing={1} alignItems="flex-start">
            <Stack spacing={0}>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                Tutoriais relacionados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Aprenda mais sobre como gerenciar aprovações
              </Typography>
            </Stack>

            <TutorialVideoButton
              video={tutorialVideoById["approving"]}
              buttonLabel="Aprovar (parte 1)"
              buttonProps={{ size: "small" }}
            />
            <TutorialVideoButton
              video={tutorialVideoById["approving-advanced"]}
              buttonLabel="Aprovar (parte 2)"
              buttonProps={{ size: "small" }}
            />
            <TutorialVideoButton
              video={tutorialVideoById["content-change"]}
              buttonLabel="Alteração de conteúdo"
              buttonProps={{ size: "small" }}
            />
          </Stack>
        </Stack>
      }
    >
      <ApprovalItemsTable />
    </PageContainerSide>
  );
};

export default Page;
